import { useMutation, useQueryClient } from 'react-query';
import { authFetch } from '../../../../../CommonUtils/authFetch';
import { httpBodyOptions } from '../../../../../CommonUtils/httpBodyOptions';
import { FinancialPlanDTO } from '../../../../types/financialPlan.types';
import { queryKeys } from '../../../../api/queryKeys';
import { queryKeys as actionStepsReportQueryKeys } from '../../../../../ActionStepsReport/api/queryKeys';
import { ActionStepsDTO } from '../../../../../ActionStepsReport/api/useActionStepsQuery';

interface RemoveTaskParams {
  clientId: number;
  fpId: string;
  ActionStepKey: string;
  GoalId: string;
  isReport?: boolean;
}

const removeTask = ({ GoalId, ActionStepKey, clientId, fpId }: RemoveTaskParams): Promise<null> =>
  authFetch('/Consumer/UpdatePlanActionSteps/Remove', {
    method: 'POST',
    ...httpBodyOptions({ GoalId, clientId, fpId, ActionStepKey }),
  });

export const useRemoveTaskMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<
    null,
    Error,
    RemoveTaskParams,
    { previousState: FinancialPlanDTO | ActionStepsDTO | undefined }
  >(removeTask, {
    onMutate: async ({ clientId, fpId, ActionStepKey, isReport }) => {
      if (isReport) {
        const queryKey = actionStepsReportQueryKeys.actionSteps(clientId, fpId);

        const previousState = queryClient.getQueryData<ActionStepsDTO | undefined>(queryKey);

        queryClient.setQueryData<ActionStepsDTO | undefined>(queryKey, old => {
          if (old) {
            return {
              ...old,
              Tasks: old.Tasks.filter(actionStep => actionStep.ActionStepKey !== ActionStepKey),
            };
          }

          return old;
        });

        return { previousState };
      }

      const queryKey = queryKeys.financialPlan(clientId, fpId);

      const previousState = queryClient.getQueryData<FinancialPlanDTO | undefined>(queryKey);

      queryClient.setQueryData<FinancialPlanDTO | undefined>(queryKey, old => {
        if (old && old.PlanActionSteps) {
          return {
            ...old,
            PlanActionSteps: old.PlanActionSteps.filter(
              actionStep => actionStep.ActionStepKey !== ActionStepKey,
            ),
          };
        }
        return old;
      });

      return { previousState };
    },

    // If the mutation fails, use the context returned from onMutate to roll back
    onError: (err, { clientId, fpId, isReport }, context) => {
      const queryKey = isReport
        ? actionStepsReportQueryKeys.actionSteps(clientId, fpId)
        : queryKeys.financialPlan(clientId, fpId);

      if (context && context.previousState) {
        queryClient.setQueryData(queryKey, context.previousState);
      }
    },
  });
};
